import React, { useEffect, useState, useCallback } from "react";
import {
  Box, Card, Typography, Grid, CircularProgress, MenuItem,
  TextField, Button, Divider,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip,
  ResponsiveContainer, CartesianGrid, PieChart, Pie, Cell, Legend,
} from "recharts";
import api from "../../utils/api";

const COLORS = ["#2f63ad","#0f766e","#b45309","#7c3aed","#dc2626","#0369a1","#65a30d"];

const GateReportsPage = () => {
  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(true);
  const [days, setDays]         = useState(30);
  const [exportFrom, setExportFrom] = useState("");
  const [exportTo, setExportTo]     = useState("");
  const [exporting, setExporting]   = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/gate-reports/traffic?days=${days}`);
      setData(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => { load(); }, [load]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (exportFrom) params.set("from", exportFrom);
      if (exportTo)   params.set("to",   exportTo);
      const token = sessionStorage.getItem("token") || localStorage.getItem("token") || "";
      const a = document.createElement("a");
      a.href = `${api.defaults.baseURL}/gate-visitors/export/csv?${params}&_t=${Date.now()}`;
      // Use fetch so we can include auth header
      const res = await fetch(a.href, { headers:{ Authorization:`Bearer ${token}` } });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      a.href = url;
      a.download = `gate-log-${Date.now()}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      alert("Export failed.");
    } finally {
      setExporting(false);
    }
  };

  const cardSx = { borderRadius:"16px", border:"1px solid rgba(47,99,173,0.08)", boxShadow:"0 4px 16px rgba(0,0,0,0.06)", p:2.5 };
  const fieldSx = { "& .MuiOutlinedInput-root":{ borderRadius:"10px" }, minWidth:120 };

  if (loading) return <Box sx={{ display:"flex", justifyContent:"center", py:6 }}><CircularProgress /></Box>;

  return (
    <Box>
      <Box sx={{ display:"flex", justifyContent:"space-between", alignItems:"center", mb:3, flexWrap:"wrap", gap:2 }}>
        <Typography variant="h5" sx={{ fontWeight:800, color:"#223142" }}>Gate Reports</Typography>
        <TextField select label="Period" value={days} onChange={e=>setDays(Number(e.target.value))} size="small" sx={fieldSx}>
          {[7,14,30,60,90].map(d=><MenuItem key={d} value={d}>Last {d} days</MenuItem>)}
        </TextField>
      </Box>

      <Grid container spacing={3}>
        {/* Daily Traffic */}
        <Grid item xs={12}>
          <Card sx={cardSx}>
            <Typography variant="subtitle1" sx={{ fontWeight:700, color:"#223142", mb:2 }}>Daily Arrivals - Last {days} Days</Typography>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={data?.daily || []} margin={{ left:-20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                <XAxis dataKey="date" tick={{ fontSize:10, fill:"#6b778c" }} interval={Math.floor((data?.daily?.length||1)/7)} />
                <YAxis tick={{ fontSize:11, fill:"#6b778c" }} allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="visits" fill="#2f63ad" radius={[4,4,0,0]} name="Visitors" />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        </Grid>

        {/* By Type Pie + Peak Hours */}
        <Grid item xs={12} md={5}>
          <Card sx={{ ...cardSx, height:"100%" }}>
            <Typography variant="subtitle1" sx={{ fontWeight:700, color:"#223142", mb:2 }}>Visitors by Type</Typography>
            {(data?.byType||[]).length === 0 ? (
              <Typography variant="body2" sx={{ color:"#6b778c" }}>No data.</Typography>
            ) : (
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie data={data.byType.map(r=>({ name: r.TypeName||"Unknown", value: r.count }))}
                    dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label>
                    {(data.byType||[]).map((_, i) => (
                      <Cell key={i} fill={COLORS[i % COLORS.length]} />
                    ))}
                  </Pie>
                  <Legend />
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            )}
          </Card>
        </Grid>

        <Grid item xs={12} md={7}>
          <Card sx={{ ...cardSx, height:"100%" }}>
            <Typography variant="subtitle1" sx={{ fontWeight:700, color:"#223142", mb:2 }}>Peak Hours (All Time)</Typography>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={(data?.peakHours||[]).map(r=>({ hour:`${r.hour}:00`, count:r.count }))} margin={{ left:-20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                <XAxis dataKey="hour" tick={{ fontSize:10, fill:"#6b778c" }} />
                <YAxis tick={{ fontSize:11, fill:"#6b778c" }} allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="count" fill="#0f766e" radius={[4,4,0,0]} name="Visits" />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        </Grid>

        {/* Export */}
        <Grid item xs={12}>
          <Card sx={cardSx}>
            <Typography variant="subtitle1" sx={{ fontWeight:700, color:"#223142", mb:2 }}>Export Gate Log</Typography>
            <Box sx={{ display:"flex", gap:2, flexWrap:"wrap", alignItems:"flex-end" }}>
              <TextField label="From Date" type="date" size="small" InputLabelProps={{ shrink:true }}
                value={exportFrom} onChange={e=>setExportFrom(e.target.value)} sx={fieldSx} />
              <TextField label="To Date" type="date" size="small" InputLabelProps={{ shrink:true }}
                value={exportTo} onChange={e=>setExportTo(e.target.value)} sx={fieldSx} />
              <Button variant="contained" startIcon={<DownloadIcon />} onClick={handleExport} disabled={exporting}
                sx={{ borderRadius:"10px", textTransform:"none", fontWeight:700, boxShadow:"none",
                  backgroundColor:"#2f63ad", "&:hover":{ backgroundColor:"#1f4f94", boxShadow:"none" } }}>
                {exporting ? "Exporting..." : "Export CSV"}
              </Button>
            </Box>
            <Typography variant="caption" sx={{ color:"#6b778c", mt:1, display:"block" }}>
              Leave dates empty to export all records.
            </Typography>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default GateReportsPage;
